'use client';

import { useState, useEffect } from 'react';
import FilterSidebar, { SelectedFilters } from './FilterSidebar';
import ItineraryCard from './ItineraryCard';
import SearchBar from './SearchBar';
import SortDropdown from './SortDropdown';
import ViewToggle from './ViewToggle';
import Icon from '@/components/ui/AppIcon';

interface Itinerary {
  id: string;
  title: string;
  destination: string;
  duration: string;
  price: number;
  originalPrice?: number;
  rating: number;
  reviewCount: number;
  image: string;
  alt: string;
  highlights: string[];
  availability: 'high' | 'medium' | 'low';
  pnrGuaranteed: boolean;
  tags: string[];
  destinationCategory: string;
  durationCategory: string;
  priceCategory: string;
  styleCategories: string[];
}

type ThemeType = 'default' | 'dark' | 'nature' | 'ocean';
type LayoutType = 'grid' | 'list' | 'compact';

const THEMES: { id: ThemeType; label: string; bg: string; accent: string }[] = [
  { id: 'default', label: 'Classic', bg: 'bg-background', accent: 'border-primary' },
  { id: 'dark', label: 'Dark Mode', bg: 'bg-gray-900', accent: 'border-purple-500' },
  { id: 'nature', label: 'Nature', bg: 'bg-green-50', accent: 'border-green-500' },
  { id: 'ocean', label: 'Ocean', bg: 'bg-blue-50', accent: 'border-blue-500' },
];

const ItineraryGalleryInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [layoutType, setLayoutType] = useState<LayoutType>('grid');
  const [selectedTheme, setSelectedTheme] = useState<ThemeType>('default');
  const [showThemePanel, setShowThemePanel] = useState(false);
  const [savedItineraries, setSavedItineraries] = useState<string[]>([]);
  const [shareToast, setShareToast] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
    destinations: [],
    durations: [],
    priceRanges: [],
    travelStyles: []
  });
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    const saved = localStorage.getItem('savedItineraries');
    if (saved) setSavedItineraries(JSON.parse(saved));
  }, []);

  const handleSave = (id: string) => {
    const updated = savedItineraries.includes(id)
      ? savedItineraries.filter(s => s !== id)
      : [...savedItineraries, id];
    setSavedItineraries(updated);
    localStorage.setItem('savedItineraries', JSON.stringify(updated));
    setShareToast(savedItineraries.includes(id) ? 'Removed from saved' : 'Saved to your list!');
    setTimeout(() => setShareToast(''), 2500);
  };

  const handleShare = (itinerary: Itinerary) => {
    const url = `${window.location.origin}/itinerary-gallery?id=${itinerary.id}`;
    if (navigator.share) {
      navigator.share({ title: itinerary.title, url });
    } else {
      navigator.clipboard.writeText(url);
      setShareToast('Link copied to clipboard!');
      setTimeout(() => setShareToast(''), 2500);
    }
  };

  const getThemeClasses = () => {
    switch (selectedTheme) {
      case 'dark': return 'bg-gray-900 text-white';
      case 'nature': return 'bg-green-50';
      case 'ocean': return 'bg-blue-50';
      default: return 'bg-background';
    }
  };

  const mockItineraries: Itinerary[] = [
    {
      id: 'it-001',
      title: 'Grand European Adventure: Paris, Rome & Barcelona',
      destination: 'Europe',
      duration: '12 Days',
      price: 4299,
      originalPrice: 4899,
      rating: 4.9,
      reviewCount: 342,
      image: 'https://images.unsplash.com/photo-1613348588446-92a408d9d4bc',
      alt: 'Illuminated Eiffel Tower at night with golden lights against dark blue sky in Paris France',
      highlights: ['Eiffel Tower skip-the-line access', 'Vatican Museums private tour', 'Sagrada Familia guided experience'],
      availability: 'high',
      pnrGuaranteed: true,
      tags: ['Cultural', 'Luxury', 'Guided Tours'],
      destinationCategory: 'europe',
      durationCategory: '10-14',
      priceCategory: 'moderate',
      styleCategories: ['cultural', 'luxury']
    },
    {
      id: 'it-002',
      title: 'Tropical Paradise: Bali & Gili Islands Escape',
      destination: 'Asia',
      duration: '8 Days',
      price: 2199,
      rating: 4.8,
      reviewCount: 287,
      image: 'https://images.unsplash.com/photo-1560585810-e523ac2ccda7',
      alt: 'Traditional Balinese temple with ornate stone carvings surrounded by lush tropical greenery and palm trees',
      highlights: ['Ubud rice terraces trek', 'Snorkeling with sea turtles', 'Beachfront villa accommodation'],
      availability: 'medium',
      pnrGuaranteed: true,
      tags: ['Relaxation', 'Beach', 'Adventure'],
      destinationCategory: 'asia',
      durationCategory: '6-9',
      priceCategory: 'moderate',
      styleCategories: ['relaxation', 'adventure']
    },
    {
      id: 'it-003',
      title: 'African Safari: Kenya & Tanzania Wildlife Experience',
      destination: 'Africa',
      duration: '10 Days',
      price: 6799,
      originalPrice: 7499,
      rating: 5.0,
      reviewCount: 198,
      image: 'https://images.unsplash.com/photo-1705909892927-a45e54fd7d72',
      alt: 'Two giraffes standing in African savanna grassland with acacia trees at golden hour sunset',
      highlights: ['Serengeti game drives', 'Masai Mara hot air balloon safari', 'Luxury tented camp stays'],
      availability: 'low',
      pnrGuaranteed: true,
      tags: ['Adventure', 'Wildlife', 'Luxury'],
      destinationCategory: 'africa',
      durationCategory: '10-14',
      priceCategory: 'luxury',
      styleCategories: ['adventure', 'luxury']
    },
    {
      id: 'it-004',
      title: 'New York City Explorer: The Big Apple Experience',
      destination: 'Americas',
      duration: '5 Days',
      price: 1899,
      rating: 4.7,
      reviewCount: 421,
      image: 'https://images.unsplash.com/photo-1580752300984-a479ef2f97dc',
      alt: 'Manhattan skyline with Empire State Building and modern skyscrapers at dusk with purple and orange sky',
      highlights: ['Broadway show tickets', 'Statue of Liberty cruise', 'Central Park guided bike tour'],
      availability: 'high',
      pnrGuaranteed: true,
      tags: ['Cultural', 'Urban', 'Entertainment'],
      destinationCategory: 'americas',
      durationCategory: '3-5',
      priceCategory: 'budget',
      styleCategories: ['cultural']
    },
    {
      id: 'it-005',
      title: 'Australian Outback & Great Barrier Reef Adventure',
      destination: 'Oceania',
      duration: '14 Days',
      price: 8499,
      rating: 4.9,
      reviewCount: 156,
      image: 'https://images.unsplash.com/photo-1651832504817-89c6456cbcf5',
      alt: 'Aerial view of Great Barrier Reef with turquoise water and coral formations visible below surface',
      highlights: ['Great Barrier Reef diving', 'Uluru sunrise experience', 'Sydney Opera House tour'],
      availability: 'medium',
      pnrGuaranteed: true,
      tags: ['Adventure', 'Nature', 'Diving'],
      destinationCategory: 'oceania',
      durationCategory: '10-14',
      priceCategory: 'luxury',
      styleCategories: ['adventure']
    },
    {
      id: 'it-006',
      title: 'Japanese Cultural Immersion: Tokyo to Kyoto',
      destination: 'Asia',
      duration: '9 Days',
      price: 3899,
      originalPrice: 4299,
      rating: 4.8,
      reviewCount: 312,
      image: 'https://images.unsplash.com/photo-1557409362-fb339d4a0cf5',
      alt: 'Traditional Japanese temple with red pagoda surrounded by pink cherry blossom trees in full bloom',
      highlights: ['Mount Fuji day trip', 'Traditional tea ceremony', 'Bullet train experience'],
      availability: 'high',
      pnrGuaranteed: true,
      tags: ['Cultural', 'Traditional', 'Culinary'],
      destinationCategory: 'asia',
      durationCategory: '6-9',
      priceCategory: 'moderate',
      styleCategories: ['cultural']
    },
    {
      id: 'it-007',
      title: 'Caribbean Island Hopping: Bahamas & Turks',
      destination: 'Americas',
      duration: '7 Days',
      price: 2799,
      rating: 4.6,
      reviewCount: 234,
      image: 'https://images.unsplash.com/photo-1672841828459-55ed27466c5a',
      alt: 'Pristine Caribbean beach with white sand, turquoise water, and palm trees swaying in tropical breeze',
      highlights: ['Private catamaran sailing', 'Swimming with pigs experience', 'Beachfront resort stays'],
      availability: 'high',
      pnrGuaranteed: true,
      tags: ['Relaxation', 'Beach', 'Luxury'],
      destinationCategory: 'americas',
      durationCategory: '6-9',
      priceCategory: 'moderate',
      styleCategories: ['relaxation', 'luxury']
    },
    {
      id: 'it-008',
      title: 'Scandinavian Northern Lights Quest',
      destination: 'Europe',
      duration: '6 Days',
      price: 3299,
      rating: 4.9,
      reviewCount: 189,
      image: 'https://images.unsplash.com/photo-1589311836849-4cec35b363cb',
      alt: 'Green and purple aurora borealis dancing over snow-covered Norwegian mountains and frozen lake',
      highlights: ['Northern Lights hunting', 'Husky sledding adventure', 'Ice hotel overnight stay'],
      availability: 'low',
      pnrGuaranteed: true,
      tags: ['Adventure', 'Nature', 'Winter'],
      destinationCategory: 'europe',
      durationCategory: '6-9',
      priceCategory: 'moderate',
      styleCategories: ['adventure']
    },
    {
      id: 'it-009',
      title: 'Machu Picchu & Amazon Rainforest Expedition',
      destination: 'Americas',
      duration: '11 Days',
      price: 4599,
      rating: 5.0,
      reviewCount: 267,
      image: 'https://images.unsplash.com/photo-1567696713881-30ed60ca0aec',
      alt: 'Ancient Incan ruins of Machu Picchu with stone terraces and temples against misty mountain backdrop',
      highlights: ['Inca Trail guided trek', 'Amazon jungle lodge stay', 'Cusco cultural exploration'],
      availability: 'medium',
      pnrGuaranteed: true,
      tags: ['Adventure', 'Cultural', 'Hiking'],
      destinationCategory: 'americas',
      durationCategory: '10-14',
      priceCategory: 'moderate',
      styleCategories: ['adventure', 'cultural']
    },
    {
      id: 'it-010',
      title: 'Dubai & Abu Dhabi Luxury Experience',
      destination: 'Asia',
      duration: '6 Days',
      price: 5299,
      originalPrice: 5899,
      rating: 4.7,
      reviewCount: 298,
      image: 'https://images.unsplash.com/photo-1651063820152-d3e7a27b4d2b',
      alt: 'Dubai skyline at sunset with Burj Khalifa tower and modern glass skyscrapers reflecting golden light',
      highlights: ['Burj Khalifa observation deck', 'Desert safari with dinner', '5-star hotel accommodations'],
      availability: 'high',
      pnrGuaranteed: true,
      tags: ['Luxury', 'Urban', 'Shopping'],
      destinationCategory: 'asia',
      durationCategory: '6-9',
      priceCategory: 'luxury',
      styleCategories: ['luxury']
    },
    {
      id: 'it-011',
      title: 'Greek Islands Sailing Adventure',
      destination: 'Europe',
      duration: '8 Days',
      price: 3599,
      rating: 4.8,
      reviewCount: 223,
      image: 'https://images.unsplash.com/photo-1618478751717-b2fc9412fd11',
      alt: 'White-washed buildings with blue domed churches overlooking Aegean Sea in Santorini Greece at sunset',
      highlights: ['Private yacht charter', 'Santorini sunset viewing', 'Ancient ruins exploration'],
      availability: 'medium',
      pnrGuaranteed: true,
      tags: ['Relaxation', 'Beach', 'Cultural'],
      destinationCategory: 'europe',
      durationCategory: '6-9',
      priceCategory: 'moderate',
      styleCategories: ['relaxation', 'cultural']
    },
    {
      id: 'it-012',
      title: 'Family Fun: Orlando Theme Parks Extravaganza',
      destination: 'Americas',
      duration: '7 Days',
      price: 2499,
      rating: 4.6,
      reviewCount: 456,
      image: 'https://images.unsplash.com/photo-1552004602-083134b4b4b4',
      alt: 'Colorful theme park castle with fireworks display lighting up night sky with families watching below',
      highlights: ['Multi-park access passes', 'Character dining experiences', 'Resort hotel with water park'],
      availability: 'high',
      pnrGuaranteed: true,
      tags: ['Family', 'Entertainment', 'Theme Parks'],
      destinationCategory: 'americas',
      durationCategory: '6-9',
      priceCategory: 'moderate',
      styleCategories: ['family']
    }
  ];

  const filterItineraries = (itineraries: Itinerary[]) => {
    let filtered = [...itineraries];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (it) =>
          it.title.toLowerCase().includes(query) ||
          it.destination.toLowerCase().includes(query) ||
          it.highlights.some((h) => h.toLowerCase().includes(query))
      );
    }
    if (selectedFilters.destinations.length > 0) {
      filtered = filtered.filter((it) => selectedFilters.destinations.includes(it.destinationCategory));
    }
    if (selectedFilters.durations.length > 0) {
      filtered = filtered.filter((it) => selectedFilters.durations.includes(it.durationCategory));
    }
    if (selectedFilters.priceRanges.length > 0) {
      filtered = filtered.filter((it) => selectedFilters.priceRanges.includes(it.priceCategory));
    }
    if (selectedFilters.travelStyles.length > 0) {
      filtered = filtered.filter((it) =>
        it.styleCategories.some((style) => selectedFilters.travelStyles.includes(style))
      );
    }
    return filtered;
  };

  const sortItineraries = (itineraries: Itinerary[]) => {
    const sorted = [...itineraries];
    switch (sortBy) {
      case 'price-low': return sorted.sort((a, b) => a.price - b.price);
      case 'price-high': return sorted.sort((a, b) => b.price - a.price);
      case 'duration-short': return sorted.sort((a, b) => parseInt(a.duration) - parseInt(b.duration));
      case 'duration-long': return sorted.sort((a, b) => parseInt(b.duration) - parseInt(a.duration));
      case 'rating': return sorted.sort((a, b) => b.rating - a.rating);
      case 'saved': return sorted.filter(it => savedItineraries.includes(it.id));
      default: return sorted;
    }
  };

  const getGridClass = () => {
    switch (layoutType) {
      case 'compact': return 'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4';
      case 'list': return 'space-y-4';
      default: return 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6';
    }
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-20">
        <div className="w-full px-4 lg:px-8 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-surface rounded-lg w-1/3"></div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <div className="h-96 bg-surface rounded-lg"></div>
              <div className="lg:col-span-3 space-y-6">
                <div className="h-12 bg-surface rounded-lg"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="h-96 bg-surface rounded-lg"></div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filteredAndSortedItineraries = sortItineraries(filterItineraries(mockItineraries));

  return (
    <div className={`min-h-screen pt-20 transition-colors duration-300 ${getThemeClasses()}`}>
      {shareToast && (
        <div className="fixed top-24 right-6 z-50 bg-primary text-white px-5 py-3 rounded-xl shadow-lg font-semibold text-sm flex items-center gap-2">
          <Icon name="CheckCircleIcon" size={18} variant="solid" />
          {shareToast}
        </div>
      )}

      <div className="w-full px-4 lg:px-8 py-12">
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className={`font-heading font-bold text-4xl md:text-5xl mb-4 ${selectedTheme === 'dark' ? 'text-white' : 'text-foreground'}`}>
              Curated Travel Experiences
            </h1>
            <p className={`text-lg max-w-3xl ${selectedTheme === 'dark' ? 'text-gray-300' : 'text-muted-foreground'}`}>
              Discover handpicked itineraries with guaranteed PNR confirmations and instant booking capabilities.
            </p>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            {savedItineraries.length > 0 && (
              <button
                onClick={() => setSortBy(sortBy === 'saved' ? 'featured' : 'saved')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm font-bold transition-all ${
                  sortBy === 'saved' ? 'bg-accent text-white border-accent' : 'bg-white border-accent text-accent hover:bg-accent/10'
                }`}
              >
                <Icon name="BookmarkIcon" size={16} variant="solid" />
                {savedItineraries.length} Saved
              </button>
            )}

            <div className="relative">
              <button
                onClick={() => setShowThemePanel(!showThemePanel)}
                className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-border rounded-lg text-sm font-bold text-foreground hover:border-primary transition-all"
              >
                <Icon name="SwatchIcon" size={16} />
                Theme
              </button>
              {showThemePanel && (
                <div className="absolute right-0 top-12 z-30 bg-white rounded-xl shadow-xl border border-border p-4 w-52">
                  <p className="text-xs font-bold text-muted-foreground mb-3 uppercase tracking-wide">Gallery Theme</p>
                  <div className="space-y-2">
                    {THEMES.map(theme => (
                      <button
                        key={theme.id}
                        onClick={() => { setSelectedTheme(theme.id); setShowThemePanel(false); }}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          selectedTheme === theme.id ? 'bg-primary/10 text-primary border border-primary' : 'hover:bg-surface text-foreground'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 ${theme.accent} ${theme.bg}`} />
                        {theme.label}
                        {selectedTheme === theme.id && <Icon name="CheckIcon" size={14} className="ml-auto text-primary" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center bg-white border-2 border-border rounded-lg overflow-hidden">
              {(['grid', 'list', 'compact'] as LayoutType[]).map(layout => (
                <button
                  key={layout}
                  onClick={() => { setLayoutType(layout); setViewMode(layout === 'list' ? 'list' : 'grid'); }}
                  title={layout.charAt(0).toUpperCase() + layout.slice(1)}
                  className={`px-3 py-2 text-sm transition-all ${
                    layoutType === layout ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-surface'
                  }`}
                >
                  <Icon
                    name={layout === 'grid' ? 'Squares2X2Icon' : layout === 'list' ? 'ListBulletIcon' : 'ViewColumnsIcon'}
                    size={16}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <aside className="hidden lg:block">
            <FilterSidebar selectedFilters={selectedFilters} onFilterChange={setSelectedFilters} />
          </aside>

          <div className="lg:col-span-3">
            <div className="mb-6 space-y-4">
              <SearchBar onSearch={setSearchQuery} />
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setShowMobileFilters(!showMobileFilters)}
                    className="lg:hidden flex items-center space-x-2 px-4 py-2.5 bg-card border border-border rounded-lg text-foreground hover:bg-surface transition-colors duration-200"
                  >
                    <Icon name="AdjustmentsHorizontalIcon" size={20} />
                    <span className="text-sm font-medium">Filters</span>
                  </button>
                  <div className="text-sm text-muted-foreground">
                    <span className={`font-semibold ${selectedTheme === 'dark' ? 'text-white' : 'text-foreground'}`}>
                      {filteredAndSortedItineraries.length}
                    </span> itineraries found
                    {sortBy === 'saved' && <span className="ml-2 text-accent font-semibold">(Saved only)</span>}
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <SortDropdown currentSort={sortBy} onSortChange={setSortBy} />
                  <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
                </div>
              </div>
            </div>

            {showMobileFilters && (
              <div className="lg:hidden mb-6 bg-card rounded-lg shadow-brand p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-semibold text-lg">Filters</h3>
                  <button onClick={() => setShowMobileFilters(false)} className="text-muted-foreground hover:text-foreground">
                    <Icon name="XMarkIcon" size={24} />
                  </button>
                </div>
                <FilterSidebar selectedFilters={selectedFilters} onFilterChange={setSelectedFilters} />
              </div>
            )}

            {filteredAndSortedItineraries.length === 0 ? (
              <div className="text-center py-16">
                <Icon name="MagnifyingGlassIcon" size={64} className="mx-auto text-muted-foreground mb-4" />
                <h3 className={`font-heading font-semibold text-xl mb-2 ${selectedTheme === 'dark' ? 'text-white' : 'text-foreground'}`}>
                  {sortBy === 'saved' ? 'No saved itineraries yet' : 'No itineraries found'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {sortBy === 'saved' ? 'Save itineraries by clicking the bookmark icon on any card' : 'Try adjusting your filters or search query'}
                </p>
                <button
                  onClick={() => { setSearchQuery(''); setSortBy('featured'); setSelectedFilters({ destinations: [], durations: [], priceRanges: [], travelStyles: [] }); }}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-heading font-semibold hover:bg-secondary transition-colors duration-200"
                >
                  Clear All Filters
                </button>
              </div>
            ) : (
              <div className={getGridClass()}>
                {filteredAndSortedItineraries.map((itinerary) => (
                  <ItineraryCard
                    key={itinerary.id}
                    itinerary={itinerary}
                    isSaved={savedItineraries.includes(itinerary.id)}
                    onSave={handleSave}
                    onShare={handleShare}
                    compact={layoutType === 'compact'}
                    theme={selectedTheme}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItineraryGalleryInteractive;